import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  FormControl,
  Grid,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { StyledSelect, StyledTextField } from "../../customComponents/styled/styledInputs";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import { useGetSessionsPaginatedQuery } from "../settings/session/sessionApiSlice";
import { useGetPaymentSummaryQuery } from "./paymentSummaryApiSlice";
import NotifyParentsDialog from "../invoices/NotifyParentsDialog";

const naira = (n) => `₦${Math.round(n || 0).toLocaleString()}`;

function StatCard({ icon, label, value, color, sub }) {
  return (
    <StyledPaper sx={{ p: 2.5, height: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: (t) => alpha(t.palette[color].main, 0.12),
            color: `${color}.main`,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography variant="formSubHeading" sx={{ mb: 0 }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </StyledPaper>
  );
}

function StatusBar({ label, count, total, color }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {count} ({pct}%)
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={color}
        sx={{ height: 6, borderRadius: 3 }}
      />
    </Box>
  );
}

function PaymentsDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [openNotify, setOpenNotify] = useState(false);

  const [sessionSearch, setSessionSearch] = useState("");
  const { data: sessionsData } = useGetSessionsPaginatedQuery({ page: 1, per_page: 10, q: sessionSearch });
  const sessions = sessionsData?.sessions ?? [];
  const [scopeSession, setScopeSession] = useState(null);
  const [scopeTermId, setScopeTermId] = useState("");
  const termOptions = scopeSession?.terms ?? [];

  const { data: summary, isFetching: summaryLoading } = useGetPaymentSummaryQuery({
    session_id: scopeSession?.id || undefined,
    term_id: scopeTermId || undefined,
  });

  const totals = summary?.totals || {};
  const statusBreakdown = summary?.status_breakdown || { paid: 0, partial: 0, unpaid: 0 };
  const notificationBreakdown = summary?.notification_breakdown || { notified: 0, not_notified: 0 };
  const classrooms = summary?.classrooms || [];
  const recentInvoices = summary?.recent_invoices || [];
  const invoiceCount = totals.invoice_count || 0;

  const goToInvoices = (params = {}) => {
    const search = new URLSearchParams();
    if (params.classroom_id) search.set("classroom_id", params.classroom_id);
    if (params.status) search.set("status", params.status);
    if (scopeSession?.id) search.set("session_id", scopeSession.id);
    if (scopeTermId) search.set("term_id", scopeTermId);
    navigate(`/invoices?${search.toString()}`);
  };

  const statusChipColor = (inv) => (inv.paid ? "success" : inv.partial_paid ? "warning" : "error");
  const statusLabel = (inv) => (inv.paid ? "Paid" : inv.partial_paid ? "Partial" : "Unpaid");

  return (
    <>
      <NotifyParentsDialog open={openNotify} setOpen={setOpenNotify} />

      <StyledPaper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="formHeading">Payment Dashboard</Typography>
            <Typography variant="body2" color="text.secondary">
              {scopeSession
                ? `Showing ${scopeSession.name}${termOptions.find((t) => String(t.id) === String(scopeTermId))?.name ? ` · ${termOptions.find((t) => String(t.id) === String(scopeTermId)).name}` : ""}`
                : summary?.current_term?.name
                ? `All time · currently in ${summary.current_term.name}`
                : "Fee collection at a glance"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
            <Box sx={{ minWidth: 160 }}>
              <Typography variant="formSubHeading" sx={{ mb: 0.5 }}>
                SESSION
              </Typography>
              <Autocomplete
                size="small"
                options={sessions}
                getOptionLabel={(o) => o.name || ""}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={scopeSession}
                onChange={(_, v) => {
                  setScopeSession(v);
                  setScopeTermId("");
                }}
                onInputChange={(_, v, reason) => {
                  if (reason === "input") setSessionSearch(v);
                }}
                filterOptions={(x) => x}
                renderInput={(params) => <StyledTextField {...params} placeholder="All time" />}
                sx={{ width: 180 }}
              />
            </Box>
            <Box sx={{ minWidth: 140 }}>
              <Typography variant="formSubHeading" sx={{ mb: 0.5 }}>
                TERM
              </Typography>
              <FormControl size="small" fullWidth>
                <StyledSelect
                  value={scopeTermId}
                  onChange={(e) => setScopeTermId(e.target.value)}
                  displayEmpty
                  disabled={!scopeSession}
                >
                  <MenuItem value="">All Terms</MenuItem>
                  {termOptions.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            </Box>
            <StyledLoadingButton
              startIcon={<NotificationsActiveOutlinedIcon />}
              onClick={() => setOpenNotify(true)}
              sx={{ textTransform: "none" }}
            >
              Notify Parents
            </StyledLoadingButton>
          </Box>
        </Box>
      </StyledPaper>

      {summaryLoading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* Hero stats */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<ReceiptLongOutlinedIcon />}
            label="Invoiced"
            value={naira(totals.invoiced)}
            color="primary"
            sub={`${invoiceCount} invoice${invoiceCount === 1 ? "" : "s"}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PaidOutlinedIcon />}
            label="Collected"
            value={naira(totals.collected)}
            color="success"
            sub={`${totals.collection_rate ?? 0}% of net invoiced`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<SellOutlinedIcon />}
            label="Discount Given"
            value={naira(totals.discount)}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<AccountBalanceWalletOutlinedIcon />}
            label="Outstanding"
            value={naira(totals.outstanding)}
            color="error"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        {/* Classroom breakdown */}
        <Grid item xs={12} md={7}>
          <StyledPaper sx={{ p: 2.5, height: "100%" }}>
            <Typography variant="formSubHeading" sx={{ mb: 1.5 }}>
              OUTSTANDING BY CLASS
            </Typography>
            {classrooms.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                No invoices for this scope yet.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxHeight: 360, overflowY: "auto" }}>
                {classrooms.map((c) => (
                  <Box
                    key={c.id ?? "none"}
                    onClick={() => c.id && goToInvoices({ classroom_id: c.id })}
                    sx={{
                      cursor: c.id ? "pointer" : "default",
                      p: 1.25,
                      borderRadius: 1.5,
                      border: "1px solid",
                      borderColor: "divider",
                      "&:hover": c.id ? { borderColor: "primary.main", bgcolor: "action.hover" } : {},
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5, gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {c.name}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                          {naira(c.collected)} / {naira(c.invoiced)}
                        </Typography>
                        {c.id && <ChevronRightIcon fontSize="small" sx={{ color: "text.disabled" }} />}
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={c.collection_rate}
                      color={c.collection_rate >= 80 ? "success" : c.collection_rate >= 40 ? "warning" : "error"}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </StyledPaper>
        </Grid>

        {/* Status + notification breakdown */}
        <Grid item xs={12} md={5}>
          <StyledPaper sx={{ p: 2.5, height: "100%" }}>
            <Typography variant="formSubHeading" sx={{ mb: 1.5 }}>
              PAYMENT STATUS
            </Typography>
            <StatusBar label="Paid" count={statusBreakdown.paid} total={invoiceCount} color="success" />
            <StatusBar label="Partially paid" count={statusBreakdown.partial} total={invoiceCount} color="warning" />
            <StatusBar label="Unpaid" count={statusBreakdown.unpaid} total={invoiceCount} color="error" />

            <Divider sx={{ my: 2 }} />

            <Typography variant="formSubHeading" sx={{ mb: 1.5 }}>
              PARENT NOTIFICATIONS
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: notificationBreakdown.not_notified > 0 ? 1.5 : 0 }}>
              <Chip size="small" color="success" variant="outlined" label={`${notificationBreakdown.notified} notified`} />
              <Chip size="small" color="warning" variant="outlined" label={`${notificationBreakdown.not_notified} not notified`} />
            </Box>
            {notificationBreakdown.not_notified > 0 && (
              <StyledLoadingButton
                size="small"
                startIcon={<NotificationsActiveOutlinedIcon />}
                onClick={() => setOpenNotify(true)}
                sx={{ textTransform: "none" }}
              >
                Notify {notificationBreakdown.not_notified} parent{notificationBreakdown.not_notified === 1 ? "" : "s"}
              </StyledLoadingButton>
            )}
          </StyledPaper>
        </Grid>
      </Grid>

      {/* Recent invoices */}
      <StyledPaper sx={{ pb: 1 }}>
        <Box sx={{ p: "15px 20px 5px" }}>
          <Typography variant="formSubHeading" sx={{ mb: 0 }}>
            RECENT INVOICES
          </Typography>
        </Box>
        {recentInvoices.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: "center" }}>
            No invoices for this scope yet.
          </Typography>
        ) : isMobile ? (
          <Box sx={{ px: 2, pb: 1 }}>
            {recentInvoices.map((inv) => (
              <Box
                key={inv.id}
                onClick={() => goToInvoices({ classroom_id: inv.classroom_id })}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5, mb: 1.5, cursor: "pointer" }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>{inv.student_name}</Typography>
                  <Chip size="small" color={statusChipColor(inv)} label={statusLabel(inv)} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {inv.classroom_name || "No class"}
                </Typography>
                <Typography variant="body2">
                  {naira(inv.total_amount)} · {naira(inv.amount_due)} due
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ borderBottom: "1.5px solid lightgrey" }}>
                  <StyledTableCell>STUDENT</StyledTableCell>
                  <StyledTableCell>CLASS</StyledTableCell>
                  <StyledTableCell>TOTAL</StyledTableCell>
                  <StyledTableCell>DUE</StyledTableCell>
                  <StyledTableCell>STATUS</StyledTableCell>
                  <StyledTableCell>NOTIFIED</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentInvoices.map((inv) => (
                  <StyledTableRow
                    key={inv.id}
                    onClick={() => goToInvoices({ classroom_id: inv.classroom_id })}
                    sx={{ cursor: "pointer" }}
                  >
                    <StyledTableCell>{inv.student_name}</StyledTableCell>
                    <StyledTableCell>{inv.classroom_name || "—"}</StyledTableCell>
                    <StyledTableCell>{naira(inv.total_amount)}</StyledTableCell>
                    <StyledTableCell>{naira(inv.amount_due)}</StyledTableCell>
                    <StyledTableCell>
                      <Chip size="small" color={statusChipColor(inv)} label={statusLabel(inv)} />
                    </StyledTableCell>
                    <StyledTableCell>
                      {inv.notified ? (
                        <Chip size="small" variant="outlined" color="success" label="Notified" />
                      ) : (
                        <Chip size="small" variant="outlined" label="Not notified" />
                      )}
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </StyledPaper>
    </>
  );
}

export default PaymentsDashboard;
